// Sprint 17 A3: Hook for deal intelligence — momentum, coaching, stall warnings
import { useState, useEffect } from "react";
import { apiFetch } from "@/lib/api-client";

export interface DealMomentum {
  direction: "accelerating" | "stable" | "decelerating";
  score: number;
  factors: Array<{
    factor: string;
    impact: "positive" | "negative" | "neutral";
    weight: number;
    description: string;
  }>;
}

export interface CoachingTip {
  id: string;
  category: "negotiation" | "timing" | "relationship" | "objection" | "competitor" | "closing";
  title: string;
  suggestion: string;
  confidence: number;
  aiGenerated: boolean;
}

export interface SimilarDeal {
  dealId: string;
  title: string;
  value: number;
  currency: string;
  outcome: "won" | "lost";
  similarity: number;
  lessonsLearned?: string;
  closedAt?: string;
}

export interface StallWarning {
  isStalled: boolean;
  daysSinceActivity: number;
  averageDaysInStage: number;
  recommendedAction?: string;
}

export interface DealIntelligence {
  momentum: DealMomentum;
  coaching: CoachingTip[];
  similarDeals: SimilarDeal[];
  patternSummary?: {
    wonCount: number;
    lostCount: number;
    winningInsight: string | null;
    warningInsight: string | null;
  };
  playbook?: {
    title: string;
    source: "stall_recovery" | "win_loss_pattern" | "momentum";
    steps: string[];
  };
  timeMachine?: DealTimeMachine;
  stallWarning: StallWarning;
  probability: number;
  expectedRevenue: number;
}

export interface DealTimeMachineEvent {
  id: string;
  type: "stage_transition" | "activity" | "event";
  title: string;
  description: string | null;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

export interface DealTimeMachine {
  dealId?: string;
  currentStage: string;
  currentStageEnteredAt: string | null;
  daysInStage: number;
  lastHumanActivityAt: string | null;
  stageHistory: Array<{
    id: string;
    fromStage: string;
    toStage: string;
    reason: string | null;
    source: string;
    timeInStageDays: number | null;
    transitionedAt: string;
  }>;
  events?: DealTimeMachineEvent[];
}

export function useDealIntelligence(dealId: string | null) {
  const [data, setData] = useState<DealIntelligence | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!dealId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    apiFetch<{ data: DealIntelligence }>(`/crm/intelligence/deals/${dealId}/momentum`)
      .then((res) => setData(res.data))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load"))
      .finally(() => setIsLoading(false));
  }, [dealId]);

  return { data, isLoading, error };
}

export function useDealTimeMachine(dealId: string | null) {
  const [data, setData] = useState<DealTimeMachine | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!dealId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    apiFetch<{ data: DealTimeMachine }>(`/crm/intelligence/deals/${dealId}/time-machine`)
      .then((res) => setData(res.data))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load"))
      .finally(() => setIsLoading(false));
  }, [dealId]);

  return { data, isLoading, error };
}
